import { groupBy, mapValues, mean, sumBy } from "lodash";

import type { ConsumptionGranularity, Meter, MeterConsumptionBucket } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Chart } from "@eisox/design-system";

import { m } from "~/paraglide/messages";

import {
  CHART_SERIES,
  SUB_METERS_STACK_ID,
  UNALLOCATED_ALERT_SHARE,
  UNALLOCATED_COLOR,
  UNALLOCATED_KEY,
} from "../consumption.constant";
import {
  BreakdownAlert,
  SeriesKey,
  type AppliedComparison,
  type Breakdown,
  type BreakdownRow,
  type BreakdownSegment,
  type ChartSeries,
  type ConsumptionRow,
} from "../types/consumption.type";
import { bucketSlot } from "./period.helper";

type Measure = NonNullable<MeterConsumptionBucket["consumption"]>;

type ComparedBucket = Partial<Pick<MeterConsumptionBucket, "start" | "index">> &
  Pick<MeterConsumptionBucket, "consumption" | "degreeDaysConsumption">;

interface BucketOptions {
  granularity: ConsumptionGranularity;
  timezone: string;
}

interface Difference {
  amount: number;
  ratio: number;
}

interface SubMeterConsumption {
  meter: Meter;
  buckets: MeterConsumptionBucket[];
}

const averageMeasure = (measures: (Measure | undefined)[]): Measure | undefined => {
  const defined = measures.filter(measure => measure !== undefined);
  if (defined.length === 0) return undefined;
  const objectives = defined.map(({ objective }) => objective).filter(objective => objective !== undefined);
  return {
    value: mean(defined.map(({ value }) => value)),
    objective: objectives.length === defined.length ? mean(objectives) : undefined,
  };
};

const comparedBucketOf = (
  { shift }: AppliedComparison,
  buckets: MeterConsumptionBucket[],
  { granularity, timezone }: BucketOptions,
): ((start: string) => ComparedBucket | undefined) => {
  if (shift) {
    const forward = { amount: -shift.amount, unit: shift.unit };
    const bySlot = new Map(buckets.map(bucket => [bucketSlot(bucket.start, granularity, timezone, forward), bucket]));
    return start => bySlot.get(bucketSlot(start, granularity, timezone));
  }
  const calendarMonth = (start: string) => dayjs(start).tz(timezone).format("MM");
  const averages = mapValues(
    groupBy(buckets, ({ start }) => calendarMonth(start)),
    monthBuckets => ({
      consumption: averageMeasure(monthBuckets.map(({ consumption }) => consumption)),
      degreeDaysConsumption: averageMeasure(monthBuckets.map(({ degreeDaysConsumption }) => degreeDaysConsumption)),
    }),
  );
  return start => averages[calendarMonth(start)];
};

const consumptionRows = (
  buckets: MeterConsumptionBucket[],
  options: BucketOptions,
  comparison?: { applied: AppliedComparison; buckets: MeterConsumptionBucket[] },
): ConsumptionRow[] => {
  const comparedBucket = comparison && comparedBucketOf(comparison.applied, comparison.buckets, options);
  const currentSlot = bucketSlot(dayjs().toISOString(), options.granularity, options.timezone);
  return buckets.map(({ start, index, consumption, degreeDaysConsumption }) => {
    const compared = comparedBucket?.(start);
    return {
      start,
      index,
      inProgress: bucketSlot(start, options.granularity, options.timezone) === currentSlot,
      consumption: consumption?.value,
      consumptionDju: degreeDaysConsumption?.value,
      comparisonIndex: compared?.index,
      comparison: compared?.consumption?.value,
      comparisonDju: compared?.degreeDaysConsumption?.value,
      objective: compared?.consumption?.objective,
      objectiveDju: compared?.degreeDaysConsumption?.objective,
    };
  });
};

const chartSeries = (rows: ConsumptionRow[], labels: Partial<Record<SeriesKey, string>>): ChartSeries[] =>
  Object.values(SeriesKey)
    .map(key => ({ key, label: labels[key] ?? "", ...CHART_SERIES[key] }))
    .filter(({ key, label }) => label && rows.some(row => row[key] !== undefined));

const difference = (rows: ConsumptionRow[], valueKey: SeriesKey, baseKey: SeriesKey): Difference | undefined => {
  const complete = rows.filter(row => !row.inProgress && row[valueKey] !== undefined && row[baseKey] !== undefined);
  const value = sumBy(complete, valueKey);
  const base = sumBy(complete, baseKey);
  return base === 0 ? undefined : { amount: value - base, ratio: (value - base) / base };
};

const hasConsumption = (buckets: MeterConsumptionBucket[]): boolean =>
  buckets.some(({ consumption }) => consumption !== undefined);

const consumptionTotal = (buckets: MeterConsumptionBucket[]): number =>
  sumBy(buckets, ({ consumption }) => consumption?.value ?? 0);

const breakdown = (meterBuckets: MeterConsumptionBucket[], subMeters: SubMeterConsumption[]): Breakdown => {
  const total = consumptionTotal(meterBuckets);
  const shareOf = (value: number) => (total === 0 ? 0 : value / total);
  const subMeterSegments = subMeters.map(({ meter, buckets }, position): BreakdownSegment => {
    const subMeterTotal = consumptionTotal(buckets);
    return {
      key: meter.id,
      label: meter.name,
      color: Chart.palette[position % Chart.palette.length] ?? UNALLOCATED_COLOR,
      kind: "bar",
      stackId: SUB_METERS_STACK_ID,
      total: subMeterTotal,
      share: shareOf(subMeterTotal),
    };
  });
  const subMetersTotal = sumBy(subMeterSegments, "total");
  const unallocated = total - subMetersTotal;
  const unallocatedShare = shareOf(unallocated);
  return {
    total,
    subMetersTotal,
    unallocated,
    unallocatedShare,
    segments: [
      ...subMeterSegments,
      ...(unallocated > 0
        ? [
            {
              key: UNALLOCATED_KEY,
              label: m.consumption_unallocated(),
              color: UNALLOCATED_COLOR,
              kind: "bar",
              stackId: SUB_METERS_STACK_ID,
              total: unallocated,
              share: unallocatedShare,
            } satisfies BreakdownSegment,
          ]
        : []),
    ],
  };
};

const breakdownAlert = ({ unallocated, unallocatedShare }: Breakdown): BreakdownAlert | undefined => {
  if (unallocated < 0) return BreakdownAlert.SUB_METERS_EXCEED;
  if (unallocatedShare > UNALLOCATED_ALERT_SHARE) return BreakdownAlert.HIGH_UNALLOCATED;
  return undefined;
};

const breakdownRows = (
  meterBuckets: MeterConsumptionBucket[],
  subMeters: SubMeterConsumption[],
  { granularity, timezone }: BucketOptions,
): BreakdownRow[] => {
  const slotOf = (start: string) => bucketSlot(start, granularity, timezone);
  const subMeterValues = subMeters.map(({ meter, buckets }) => ({
    meterId: meter.id,
    bySlot: new Map(buckets.map(({ start, consumption }) => [slotOf(start), consumption?.value])),
  }));
  return meterBuckets.map(({ start, consumption }) => {
    const values = subMeterValues.map(({ meterId, bySlot }) => [meterId, bySlot.get(slotOf(start))] as const);
    const allocated = sumBy(values, ([, value]) => value ?? 0);
    return {
      start,
      ...Object.fromEntries(values),
      [UNALLOCATED_KEY]: consumption === undefined ? undefined : Math.max(0, consumption.value - allocated),
    };
  });
};

export {
  breakdown,
  breakdownAlert,
  breakdownRows,
  chartSeries,
  consumptionRows,
  consumptionTotal,
  difference,
  hasConsumption,
};
